
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, X, Calendar, User, UserRound, Users, Info, MicIcon, AlertCircle, ArrowUpDown } from 'lucide-react';
import { KeyEvent, Person, Witness, EventsAndPeople } from '@/hooks/useFormState';
import VoiceRecorder from '@/components/VoiceRecorder';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useIsMobile } from '@/hooks/use-mobile';

interface EventsAndPeopleManagerProps {
  data: EventsAndPeople;
  onChange: (updatedData: EventsAndPeople) => void;
  deepgramApiKey: string;
}

const EventsAndPeopleManager: React.FC<EventsAndPeopleManagerProps> = ({ 
  data, 
  onChange,
  deepgramApiKey
}) => {
  const isMobile = useIsMobile();
  
  // Generate a unique ID for new items
  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Key Events Management
  const addKeyEvent = () => {
    const newEvent: KeyEvent = {
      id: generateId(),
      date: '',
      description: ''
    };
    onChange({
      ...data,
      keyEvents: [...data.keyEvents, newEvent]
    });
  };

  const updateKeyEvent = (id: string, field: keyof KeyEvent, value: string) => {
    onChange({
      ...data,
      keyEvents: data.keyEvents.map(event => 
        event.id === id ? { ...event, [field]: value } : event
      )
    });
  };

  const removeKeyEvent = (id: string) => {
    onChange({
      ...data,
      keyEvents: data.keyEvents.filter(event => event.id !== id)
    });
  };

  const handleEventTranscription = (id: string, text: string) => {
    // Get current description
    const event = data.keyEvents.find(e => e.id === id);
    if (!event) return;

    const currentText = event.description || '';
    // Append the transcribed text to the existing content
    updateKeyEvent(
      id, 
      'description', 
      currentText ? `${currentText}\n\n${text}` : text
    );
  };

  // People Involved Management
  const addPersonInvolved = () => {
    const newPerson: Person = {
      id: generateId(),
      name: '',
      role: '',
      involvement: ''
    };
    onChange({
      ...data,
      peopleInvolved: [...data.peopleInvolved, newPerson]
    });
  };

  const updatePersonInvolved = (id: string, field: keyof Person, value: string) => {
    onChange({
      ...data,
      peopleInvolved: data.peopleInvolved.map(person => 
        person.id === id ? { ...person, [field]: value } : person
      )
    });
  };

  const removePersonInvolved = (id: string) => {
    onChange({
      ...data,
      peopleInvolved: data.peopleInvolved.filter(person => person.id !== id)
    });
  };

  // Witnesses Management
  const addWitness = () => {
    const newWitness: Witness = {
      id: generateId(),
      name: '',
      role: ''
    };
    onChange({
      ...data,
      witnesses: [...data.witnesses, newWitness]
    });
  };

  const updateWitness = (id: string, field: keyof Witness, value: string) => {
    onChange({
      ...data,
      witnesses: data.witnesses.map(witness => 
        witness.id === id ? { ...witness, [field]: value } : witness
      )
    });
  };

  const removeWitness = (id: string) => {
    onChange({
      ...data,
      witnesses: data.witnesses.filter(witness => witness.id !== id)
    });
  };

  const hasKeyEvents = data.keyEvents.length > 0;
  const hasValidKeyEvents = data.keyEvents.length > 0 && 
    data.keyEvents.every(event => event.date && event.description);

  return (
    <div className="space-y-6">
      {/* Top Required Notice - Always visible and prominent */}
      <Alert className="bg-red-50 border border-red-300 p-4">
        <AlertCircle className="h-5 w-5 text-red-600" />
        <AlertDescription className="font-bold text-base">
          You MUST add at least one key event with date and description before proceeding.
        </AlertDescription>
      </Alert>

      {/* Key Events Section */}
      <div className="space-y-4">
        <div className="bg-primary/10 py-3 px-4 rounded-lg">
          <h3 className="text-xl font-medium flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-primary" />
            Key Events <span className="text-red-500 ml-1">*</span> <span className="text-red-600 ml-2 text-sm">(REQUIRED)</span>
          </h3>
          <p className="text-base text-muted-foreground mt-1">
            Add important events and their dates related to this discrimination
          </p>
        </div>

        <div className="space-y-6">
          {data.keyEvents.length === 0 ? (
            <div className="text-center py-8 flex flex-col items-center">
              <Calendar className="h-14 w-14 text-primary/30 mb-3" />
              <div className="text-muted-foreground">
                <p className="font-semibold text-red-500 text-lg">No events added yet</p>
                <p className="mt-2 text-base">You must add at least one key event to continue</p>
              </div>
              <Button 
                type="button" 
                className="mt-6 bg-primary hover:bg-primary/90 text-base py-6 px-6"
                onClick={addKeyEvent}
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Key Event Now
              </Button>
            </div>
          ) : (
            data.keyEvents.map((event, index) => (
              <div key={event.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 sm:p-5">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-lg font-medium">Event {index + 1}</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-9 w-9 p-0 rounded-full" 
                    onClick={() => removeKeyEvent(event.id)}
                  >
                    <X className="h-5 w-5" />
                    <span className="sr-only">Remove event</span>
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor={`event-date-${event.id}`} className="text-base flex items-center">
                      Date <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id={`event-date-${event.id}`}
                      type="date"
                      value={event.date}
                      onChange={(e) => updateKeyEvent(event.id, 'date', e.target.value)}
                      className={`${!event.date ? 'border-red-300 ring-1 ring-red-300' : ''} h-16 text-base`}
                      required
                    />
                    {!event.date && (
                      <p className="text-sm text-red-500 mt-1">Date is required</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor={`event-description-${event.id}`} className="text-base flex items-center">
                      Description <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Textarea
                      id={`event-description-${event.id}`}
                      value={event.description}
                      onChange={(e) => updateKeyEvent(event.id, 'description', e.target.value)}
                      className={`min-h-[200px] text-base ${!event.description ? 'border-red-300 ring-1 ring-red-300' : ''}`}
                      placeholder="Describe what happened in detail..."
                      required
                    />
                    {!event.description && (
                      <p className="text-sm text-red-500 mt-1">Description is required</p>
                    )}
                    
                    {/* Voice recording for event description */}
                    <div className="mt-4">
                      <VoiceRecorder 
                        onTranscriptionComplete={(text) => handleEventTranscription(event.id, text)}
                        apiKey={deepgramApiKey}
                        placeholder="Record details about this event..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <Button 
          type="button" 
          variant="outline" 
          size="lg" 
          className="w-full py-5 text-base" 
          onClick={addKeyEvent}
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Another Key Event
        </Button>
      </div>

      {/* People Involved Section */}
      <div className="space-y-4 mt-8">
        <div className="bg-secondary/10 py-3 px-4 rounded-lg">
          <h3 className="text-xl font-medium flex items-center">
            <User className="h-5 w-5 mr-2 text-primary" />
            People Involved <span className="text-muted-foreground ml-1 text-sm font-normal">(optional but recommended)</span>
          </h3>
          <p className="text-base text-muted-foreground mt-1">
            Add individuals who were directly involved in the discrimination incidents
          </p>
        </div>

        <div className="space-y-4">
          {data.peopleInvolved.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-base bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              No people added yet. Use the button below to add individuals who were involved.
            </div>
          ) : (
            data.peopleInvolved.map((person, index) => (
              <Collapsible key={person.id} className="bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-lg font-medium">Person {index + 1}</h4>
                    <div className="flex items-center space-x-1">
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-9 px-3 text-sm flex items-center">
                          <ArrowUpDown className="h-4 w-4 mr-2" />
                          Details
                        </Button>
                      </CollapsibleTrigger>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-9 w-9 p-0 rounded-full" 
                        onClick={() => removePersonInvolved(person.id)}
                      >
                        <X className="h-5 w-5" />
                        <span className="sr-only">Remove person</span>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor={`person-name-${person.id}`} className="text-base">
                        Name
                      </Label>
                      <Input
                        id={`person-name-${person.id}`}
                        value={person.name}
                        onChange={(e) => updatePersonInvolved(person.id, 'name', e.target.value)}
                        className="h-16 text-base"
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`person-role-${person.id}`} className="text-base">
                        Role
                      </Label>
                      <Input
                        id={`person-role-${person.id}`}
                        value={person.role}
                        onChange={(e) => updatePersonInvolved(person.id, 'role', e.target.value)}
                        className="h-16 text-base"
                        placeholder="e.g. Manager, Colleague"
                      />
                    </div>
                  </div>
                  
                  <CollapsibleContent className="mt-4 pt-4 border-t">
                    <div>
                      <Label htmlFor={`person-involvement-${person.id}`} className="text-base">
                        Involvement Details
                      </Label>
                      <Textarea
                        id={`person-involvement-${person.id}`}
                        value={person.involvement}
                        onChange={(e) => updatePersonInvolved(person.id, 'involvement', e.target.value)}
                        className="min-h-[150px] text-base"
                        placeholder="How they were involved..."
                      />
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))
          )}
        </div>

        <Button 
          type="button" 
          variant="outline" 
          size="lg" 
          className="w-full py-5 text-base" 
          onClick={addPersonInvolved}
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Person Involved
        </Button>
      </div>

      {/* Witnesses Section */}
      <div className="space-y-4 mt-8">
        <div className="bg-secondary/10 py-3 px-4 rounded-lg">
          <h3 className="text-xl font-medium flex items-center">
            <Users className="h-5 w-5 mr-2 text-primary" />
            Witnesses <span className="text-muted-foreground ml-1 text-sm font-normal">(optional but recommended)</span>
          </h3>
          <p className="text-base text-muted-foreground mt-1">
            Add people who witnessed the discrimination incidents
          </p>
        </div>

        <div className="space-y-4">
          {data.witnesses.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-base bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              No witnesses added yet. Use the button below to add witnesses.
            </div>
          ) : (
            data.witnesses.map((witness, index) => (
              <div key={witness.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-lg font-medium">Witness {index + 1}</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-9 w-9 p-0 rounded-full" 
                    onClick={() => removeWitness(witness.id)}
                  >
                    <X className="h-5 w-5" />
                    <span className="sr-only">Remove witness</span>
                  </Button>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor={`witness-name-${witness.id}`} className="text-base">
                      Name
                    </Label>
                    <Input
                      id={`witness-name-${witness.id}`}
                      value={witness.name}
                      onChange={(e) => updateWitness(witness.id, 'name', e.target.value)}
                      className="h-16 text-base"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`witness-role-${witness.id}`} className="text-base">
                      Role
                    </Label>
                    <Input
                      id={`witness-role-${witness.id}`}
                      value={witness.role}
                      onChange={(e) => updateWitness(witness.id, 'role', e.target.value)}
                      className="h-16 text-base"
                      placeholder="e.g. Colleague, Customer"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <Button 
          type="button" 
          variant="outline" 
          size="lg" 
          className="w-full py-5 text-base" 
          onClick={addWitness}
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Witness
        </Button>
      </div>
    </div>
  );
};

export default EventsAndPeopleManager;
